import BaseService from '@/services/baseService'

export const SOURCE_MODES = Object.freeze({
  REUSE: 'REUSE',
  FETCH_NEW: 'FETCH_NEW'
})

export default class PodiumReprocessService extends BaseService {
  /**
   * POST /podium/internal/reprocess
   *
   * Exactly one of sampleId / resultId must be supplied, and at least one of
   * bio / survey / lab must be FETCH_NEW. Overrides are only accepted for
   * sources set to FETCH_NEW — see SampleReprocessService pre-flight validation.
   */
  static async reprocess({ sampleId, resultId, bio, survey, lab, overrides, requestedBy, contentSnapshotId, reason }) {
    const response = await super.callStore('reprocessSample', {
      sampleId: sampleId || null,
      resultId: resultId || null,
      bio,
      survey,
      lab,
      overrides,
      requestedBy,
      ...(contentSnapshotId ? { contentSnapshotId, reason } : {})
    })

    return response.data
  }

  static async getStatus(trackingId) {
    if (!trackingId) throw new Error('Missing trackingId.')

    const response = await super.callStore('getReprocessStatus', { trackingId })

    return response.data
  }

  /**
   * Staged snapshots from the debug index, labelled from the CMS registry
   * (/podium/internal/cms/snapshots). The registry is admin-only, so if it
   * fails the index is returned unlabelled.
   */
  static async getSnapshots() {
    const [index, registry] = await Promise.all([
      super.callStore('getCmsSnapshotIndex'),
      super.callStore('getCmsSnapshotRegistry').catch(() => null)
    ])
    const data = index.data || {}
    return {
      ...data,
      snapshots: mergeSnapshotRegistry(data.snapshots || [], registry ? registry.data : null)
    }
  }
}

export function shortSnapshotId(snapshotId) {
  return snapshotId.length > 12 ? snapshotId.slice(0, 8) : snapshotId
}

/**
 * Adds `label` and `routingSource` from the registry summaries to each index
 * entry, matched by snapshot ID. The registry's `isActive` (the DB active
 * snapshot) wins over the index's when the snapshot is registered.
 */
export function mergeSnapshotRegistry(indexSnapshots, registry) {
  const byId = new Map((registry || []).map((summary) => [summary.snapshot.snapshotId, summary]))

  return indexSnapshots.map((snapshot) => {
    const summary = byId.get(snapshot.snapshotId)
    if (!summary) return { ...snapshot, label: null, routingSource: null }
    return {
      ...snapshot,
      label: summary.snapshot.label || null,
      routingSource: summary.routingSource || null,
      isActive: summary.isActive
    }
  })
}
